import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Row, Col, Card, Statistic, Spin, Table, Select, Input, InputNumber, Space, Tag, Radio, DatePicker, Button, Segmented, Switch, Tooltip as AntTooltip, Alert, Tabs } from 'antd';
import {
  DollarOutlined,
  ProjectOutlined,
  ThunderboltOutlined,
  SearchOutlined,
  RiseOutlined,
  DatabaseOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  PieChart, Pie, Cell, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import dayjs from 'dayjs';
import PageLayout from '../components/layout/PageLayout';
import AIChatBox from '../components/common/AIChatBox';
import CurrentPipeline from './retail/CurrentPipeline';
import Trending from './retail/Trending';
import api from '../libs/api';

const { RangePicker } = DatePicker;

// Fiscal quarter helpers (FY Jul-Jun: Q1=Jul-Sep, Q2=Oct-Dec, Q3=Jan-Mar, Q4=Apr-Jun)
function getFiscalQuarter(d) {
  const m = d.month(); // 0-based
  if (m >= 6 && m <= 8) return { q: 1, start: d.year(), startM: 6, endM: 8 };
  if (m >= 9 && m <= 11) return { q: 2, start: d.year(), startM: 9, endM: 11 };
  if (m >= 0 && m <= 2) return { q: 3, start: d.year(), startM: 0, endM: 2 };
  return { q: 4, start: d.year(), startM: 3, endM: 5 };
}

function getFiscalYearStart(d) {
  return d.month() >= 6
    ? dayjs().year(d.year()).month(6).startOf('month')
    : dayjs().year(d.year() - 1).month(6).startOf('month');
}

const now = dayjs();
const curQ = getFiscalQuarter(now);
const prevQDate = dayjs().month(curQ.startM).startOf('month').subtract(1, 'day');
const prevQ = getFiscalQuarter(prevQDate);

const fyStart = getFiscalYearStart(now);
const fyEnd = dayjs(fyStart).add(1, 'year').subtract(1, 'day').endOf('day'); // Jun 30
const lastFyStart = fyStart.subtract(1, 'year');
const lastFyEnd = fyStart.subtract(1, 'day').endOf('day');

const DATE_PRESETS = [
  { label: 'This Month', value: [now.startOf('month'), now.endOf('month')] },
  { label: 'Last Month', value: [now.subtract(1, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')] },
  { label: 'This Quarter', value: [dayjs().year(curQ.start).month(curQ.startM).startOf('month'), dayjs().year(curQ.start).month(curQ.endM).endOf('month')] },
  { label: 'Last Quarter', value: [dayjs().year(prevQ.start).month(prevQ.startM).startOf('month'), dayjs().year(prevQ.start).month(prevQ.endM).endOf('month')] },
  { label: 'Last 6 Months', value: [now.subtract(6, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')] },
  { label: `This Year (FY${fyStart.year()}/${fyStart.year() + 1})`, value: [fyStart, fyEnd] },
  { label: `Last Year (FY${lastFyStart.year()}/${lastFyStart.year() + 1})`, value: [lastFyStart, lastFyEnd] },
];

const fmt = (v) => {
  if (v == null) return '-';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Number(v).toFixed(0)}`;
};

const dateFmt = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

const pctFmt = (v) => (v != null ? `${Number(v).toFixed(1)}%` : '-');

// Exact dollars — the CRM STC lines are reconciliation figures, so they show
// cents rather than the $x.xK shorthand used for headline numbers.
const money2 = (v) => v == null ? '-' : `$${Number(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dayMonthYear = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

// The CRM's own STC settlement record, rendered under the Xero source note.
// Each half reports separately because they're claimed and paid independently.
// A project that can never earn a certificate reads "n/a", not "not yet
// collected" — solar STCs need panels, and battery STCs only exist under the
// scheme that started 1 Jul 2025 (see stcApplicable / bstcApplicable).
function buildCrmStcLines(r) {
  if (!r) return [];
  const stcAt  = dayMonthYear(r.crmStcCollectedAt);
  const bstcAt = dayMonthYear(r.crmBstcCollectedAt);
  const stcOk  = r.stcApplicable !== false;
  const bstcOk = r.bstcApplicable !== false;

  const lines = [
    { text: `CRM STC Record Total ${money2(r.crmStcTotal || 0)}`, strong: true },
  ];

  // Both outstanding and both claimable → one line instead of two
  if (stcOk && bstcOk && !stcAt && !bstcAt) {
    lines.push({ text: 'STC and bSTC not yet collected', pending: true });
    return lines;
  }

  if (stcAt) {
    lines.push({ text: `STC collected ${money2(r.crmStcActual)} at ${stcAt}` });
  } else if (stcOk) {
    lines.push({ text: 'STC not yet collected', pending: true });
  } else {
    lines.push({ text: 'STC n/a — battery-only system (no panels)', na: true });
  }

  if (bstcAt) {
    lines.push({ text: `bSTC collected ${money2(r.crmBstcActual)} at ${bstcAt}` });
  } else if (bstcOk) {
    lines.push({ text: 'bSTC not yet collected', pending: true });
  } else {
    lines.push({
      // battery capacity reaches this page as batterySize — the field the
      // Batt (kWh) column reads. battKwh is the PPA/commercial name for it
      // and is undefined here, which made every one of these read "no
      // battery", including the battery projects installed before the scheme.
      text: r.batterySize > 0
        ? 'bSTC n/a — installed before the 1 Jul 2025 scheme'
        : 'bSTC n/a — no battery',
      na: true,
    });
  }

  /**
   * The state battery subsidy, where the project has one.
   *
   * Named with its scheme and payer because the two behave differently and
   * both appear in the clearing account under their own reference — SA and
   * NSW settled by MAC Trade Services against the battery quote number,
   * Victoria by Solar Victoria against the INS application number. Showing
   * the reference is what lets someone match it to the bank line.
   */
  if (r.subsidyApplicable) {
    const subAt = dayMonthYear(r.subsidyFinalisedAt);
    const scheme = r.subsidyScheme === 'VIC' ? 'Vic rebate' : 'SA/NSW subsidy';
    const ref = r.subsidyReference ? ` · ref ${r.subsidyReference}` : '';
    if (subAt) {
      lines.push({ text: `${scheme} ${money2(r.subsidyAmount)} finalised at ${subAt} — ${r.subsidyPayer}${ref}` });
    } else {
      lines.push({ text: `${scheme} ${money2(r.subsidyAmount)} not yet finalised — ${r.subsidyPayer}${ref}`, pending: true });
    }
  } else if (r.stcHasSubsidy) {
    /**
     * Billed in Xero, absent from the CRM.
     *
     * True of twenty-six of the thirty-one projects carrying a subsidy on the
     * clearing account. There is no amount, scheme, reference or finalised
     * date to show because the CRM holds none — so the gap is named rather
     * than left as a missing line, which reads as "no subsidy" and is the
     * opposite of what is true.
     */
    lines.push({
      text: `State subsidy ${money2(r.stcBilledSubsidy)} billed to the clearing account, `
        + 'but the CRM has no record of it — no scheme, reference or finalised date',
      pending: true,
    });
  }
  return lines;
}

/**
 * A suburb as the CRM holds it, made readable.
 *
 * The field is typed by hand and arrives as "KEIRAVILLE", "belair",
 * "GLENALTA SA", "Towradgi," and "Encounter Bay, South Australia". Shown raw it
 * looks like three different columns. The state suffix goes because the row
 * already has a State column, and Mc is cased properly because McCracken is a
 * real suburb here and "Mccracken" reads as a typo we introduced.
 */
const suburbName = (raw) => {
  if (!raw) return '';
  const cleaned = String(raw)
    .replace(/[,\s]+$/, '')
    .replace(/,?\s*(NSW|VIC|QLD|SA|WA|TAS|NT|ACT|New South Wales|Victoria|Queensland|South Australia|Western Australia|Tasmania|Northern Territory|Australian Capital Territory)$/i, '')
    .trim();
  if (!cleaned) return '';
  return cleaned
    .split(/\s+/)
    .map((w) => {
      const lower = w.toLowerCase();
      if (/^mc[a-z]/.test(lower)) return `Mc${lower.charAt(2).toUpperCase()}${lower.slice(3)}`;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
};

/**
 * One letter, fixed width so the column reads as a stripe down the table rather
 * than a ragged edge. The word is in the tooltip; a table of a hundred rows does
 * not need "metro" spelled out a hundred times.
 */
const locPill = (cls) => ({
  display: 'inline-block',
  minWidth: 17,
  textAlign: 'center',
  fontSize: 10,
  fontWeight: 600,
  lineHeight: '16px',
  borderRadius: 3,
  flex: 'none',
  color: cls === 'rural' ? '#9a5b12' : (cls === 'metro' ? '#0b5f88' : '#8c8c8c'),
  background: cls === 'rural' ? '#fdf3e6' : (cls === 'metro' ? '#e7f2f8' : '#f0f0f0'),
});

const stateColors = { SA: 'blue', NSW: 'green', VIC: 'purple', QLD: 'orange', WA: 'red', TAS: 'cyan', NT: 'magenta', ACT: 'geekblue' };

const PIE_COLORS = ['#54A2CC', '#05A677', '#f5b759', '#FA5252', '#845EC2', '#FF6F91', '#0081CF', '#FFC75F', '#008E9B', '#C34A36'];

const columns = [
  { title: 'Project ID', dataIndex: 'projectId', key: 'projectId', width: 90, responsive: ['md'], fixed: 'left' },
  { title: 'Customer', dataIndex: 'customerName', key: 'customerName', ellipsis: true, width: 170, fixed: 'left' },
  { title: 'State', dataIndex: 'state', key: 'state', width: 65, render: (v) => v ? <Tag color={stateColors[v] || 'default'}>{v}</Tag> : '-' },
  {
    // Postcode alongside the class, because "why is this one rural" should be
    // answerable from the row rather than by asking.
    title: 'Location', dataIndex: 'suburb', key: 'locationClass', width: 160,
    filters: [{ text: 'Metro', value: 'metro' }, { text: 'Rural', value: 'rural' }],
    onFilter: (v, r) => r.locationClass === v,
    sorter: (a, b) => String(a.suburb || '').localeCompare(String(b.suburb || '')),
    render: (v, r) => (
      <span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: '#171c22' }}>{suburbName(v) || '—'}</span>
          {r.locationClass
            ? (
              <AntTooltip title={r.locationClass === 'rural' ? 'Rural' : 'Metro'}>
                <span style={locPill(r.locationClass)}>{r.locationClass === 'rural' ? 'R' : 'M'}</span>
              </AntTooltip>
            )
            : (
              <AntTooltip title="No usable postcode, so it is counted as neither">
                <span style={locPill(null)}>?</span>
              </AntTooltip>
            )}
        </div>
        <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.postcode || '—'}</div>
      </span>
    ),
    responsive: ['xl'],
  },
  {
    title: 'Cust. hold', dataIndex: 'customerHoldDays', key: 'customerHoldDays',
    align: 'right', width: 100,
    sorter: (a, b) => (a.customerHoldDays || 0) - (b.customerHoldDays || 0),
    filters: [
      { text: 'Has a customer hold', value: 'any' },
      { text: 'Dates need checking', value: 'suspect' },
    ],
    onFilter: (v, r) => (v === 'suspect' ? !!r.customerHoldSuspect : r.customerHoldDays > 0),
    render: (v, r) => (v
      ? (
        <AntTooltip
          title={r.customerHoldSuspect
            ? `${r.customerHoldSuspectReason}. Left out of the cycle-time figures until the record is corrected.`
            : (r.customerHoldOpen ? 'Still on hold — measured to today' : null)}
        >
          <span style={{ color: r.customerHoldSuspect ? '#FA5252' : '#f5b759', whiteSpace: 'nowrap' }}>
            {v}d{r.customerHoldOpen && !r.customerHoldSuspect ? '+' : ''}
            {r.customerHoldSuspect && (
              <WarningOutlined style={{ marginLeft: 4 }} />
            )}
          </span>
        </AntTooltip>
      )
      : <span style={{ color: '#bfbfbf' }}>-</span>),
    responsive: ['xl'],
  },
  { title: 'System', dataIndex: 'systemType', key: 'systemType', width: 150, render: (v) => v || '-', responsive: ['lg'] },
  { title: 'Payment', dataIndex: 'paymentMethod', key: 'paymentMethod', width: 120, render: (v) => v || '-', responsive: ['lg'] },
  { title: 'CRM Rev', dataIndex: 'revenue', key: 'revenue', render: fmt, align: 'right', width: 100, sorter: (a, b) => a.revenue - b.revenue },
  { title: 'CRM Payment', dataIndex: 'payment', key: 'payment', render: fmt, align: 'right', width: 110, responsive: ['md'] },
  { title: 'Acct Rev', dataIndex: 'acctRevenue', key: 'acctRevenue', render: fmt, align: 'right', width: 100 },
  {
    title: 'Acct GP', dataIndex: 'gp', key: 'gp', align: 'right', width: 100, sorter: (a, b) => a.gp - b.gp,
    render: (v) => <span style={{ color: v >= 0 ? '#05A677' : '#FA5252', fontWeight: 600 }}>{fmt(v)}</span>,
  },
  {
    title: 'Acct GP%', dataIndex: 'gpPct', key: 'gpPct', align: 'right', width: 80,
    render: (v) => {
      if (v == null) return '-';
      let color = '#05A677'; // green (>20%)
      if (v < 10) color = '#FA5252'; // red (<10%)
      else if (v < 20) color = '#f5b759'; // yellow (10-20%)
      return <span style={{ color }}>{pctFmt(v)}</span>;
    },
    sorter: (a, b) => (a.gpPct ?? -999) - (b.gpPct ?? -999),
  },
  {
    title: 'CRM GP%', key: 'crmGpRange', align: 'right', width: 110,
    render: (_, record) => {
      const { gpMax, gpMin, revenue } = record;
      if (!gpMax || !gpMin || !revenue || revenue === 0) return '-';
      const pctMax = (gpMax / revenue) * 100;
      const pctMin = (gpMin / revenue) * 100;
      return `${pctMin.toFixed(1)}% ~ ${pctMax.toFixed(1)}%`;
    },
    responsive: ['xl'],
  },
  {
    title: 'ocQuoteID', dataIndex: 'ocQuoteID', key: 'ocQuoteID', width: 100,
    render: (v) => v || '-',
    responsive: ['md'],
  },
  {
    title: 'CRM GP_max', dataIndex: 'gpMax', key: 'gpMax', align: 'right', width: 110,
    render: (v) => v != null ? fmt(v) : '-',
    sorter: (a, b) => (a.gpMax || 0) - (b.gpMax || 0),
    responsive: ['xl'],
  },
  {
    title: 'CRM GP_min', dataIndex: 'gpMin', key: 'gpMin', align: 'right', width: 110,
    render: (v) => v != null ? fmt(v) : '-',
    sorter: (a, b) => (a.gpMin || 0) - (b.gpMin || 0),
    responsive: ['xl'],
  },
  { title: 'PV (kW)', dataIndex: 'pvSize', key: 'pvSize', align: 'right', width: 80, render: (v) => v ? v.toFixed(1) : '-', responsive: ['xl'] },
  { title: 'Batt (kWh)', dataIndex: 'batterySize', key: 'batterySize', align: 'right', width: 90, render: (v) => v ? v.toFixed(1) : '-', responsive: ['xl'] },
  { title: 'Closing', dataIndex: 'closingDate', key: 'closingDate', render: dateFmt, width: 100, responsive: ['xl'] },
  { title: 'Install', dataIndex: 'installationAt', key: 'installationAt', render: dateFmt, width: 100, responsive: ['xl'] },
  {
    title: 'Sale→Job', dataIndex: 'daysToJob', key: 'daysToJob', align: 'right', width: 80,
    render: (v) => v != null ? `${v}d` : '-',
    sorter: (a, b) => (a.daysToJob ?? 9999) - (b.daysToJob ?? 9999),
    responsive: ['xl'],
  },
  {
    title: 'Job→WP', dataIndex: 'jobToWelcome', key: 'jobToWelcome', align: 'right', width: 80,
    render: (v) => v != null ? `${v}d` : '-',
    sorter: (a, b) => (a.jobToWelcome ?? 9999) - (b.jobToWelcome ?? 9999),
    responsive: ['xl'],
  },
  {
    title: 'Cls→Inst', dataIndex: 'daysToInstall', key: 'daysToInstall', align: 'right', width: 80,
    render: (v) => v != null ? `${v}d` : '-',
    sorter: (a, b) => (a.daysToInstall ?? 9999) - (b.daysToInstall ?? 9999),
    responsive: ['xl'],
  },
];

// Percentile helper (for client-side filtered stats)
function percentile(arr, p) {
  if (!arr.length) return null;
  const sorted = [...arr].sort((a, b) => a - b);
  const idx = Math.ceil(sorted.length * p / 100) - 1;
  return sorted[Math.min(idx, sorted.length - 1)];
}

// Build histogram bins for duration data
/**
 * Two distributions over one set of buckets, so the bars can be compared.
 *
 * buildHistogram sizes its buckets to its own data, so two independent calls
 * produce two different axes and bars that look comparable without being so.
 * This spans both series and fills every bucket in between for each.
 *
 * Fortnightly buckets read well over the three or four months most projects
 * take, and badly over the one that took two years: filling every bucket to
 * the longest value would draw fifty columns to show six projects. Everything
 * past the cap is gathered into a final open-ended bucket instead, so the
 * shape stays legible and no project is dropped to get it.
 */
const PAIRED_MAX_BUCKETS = 18;
/**
 * One height for every chart body on this page.
 *
 * The four charts sit in two rows of two and are read across as well as down,
 * so they are given the same box rather than each being sized to its own
 * content — a caption or a longer legend under one of them would otherwise
 * push it out of line with the rest.
 */
const CHART_BOX = 260;

function buildPairedHistogram(a, b, binSize = 14) {
  if (!a.length && !b.length) return [];
  const raw = (v) => Math.floor(Math.max(v, 0) / binSize) * binSize;
  const highest = Math.max(0, ...a.map(raw), ...b.map(raw));
  const cap = Math.min(highest, (PAIRED_MAX_BUCKETS - 1) * binSize);
  const bucket = (v) => Math.min(raw(v), cap);
  const rows = [];
  for (let start = 0; start <= cap; start += binSize) {
    const overflow = start === cap && highest > cap;
    rows.push({
      range: overflow ? `${start}+` : `${start}-${start + binSize}`,
      order: start,
      Awaiting: a.filter((v) => bucket(v) === start).length,
      Completed: b.filter((v) => bucket(v) === start).length,
    });
  }
  return rows;
}

function buildHistogram(values, binSize = 30) {
  if (!values.length) return [];
  const max = Math.max(...values);
  const bins = {};
  for (const v of values) {
    const bin = Math.floor(v / binSize) * binSize;
    const label = `${bin}-${bin + binSize}`;
    if (!bins[label]) bins[label] = { range: label, count: 0, order: bin };
    bins[label].count += 1;
  }
  // Fill gaps
  for (let b = 0; b <= max; b += binSize) {
    const label = `${b}-${b + binSize}`;
    if (!bins[label]) bins[label] = { range: label, count: 0, order: b };
  }
  return Object.values(bins).sort((a, b) => a.order - b.order);
}

function groupByField(projects, field) {
  const map = {};
  let total = 0;
  for (const p of projects) {
    const key = p[field] || 'Unknown';
    if (!map[key]) map[key] = { name: key, value: 0, count: 0 };
    map[key].value += p.revenue || 0;
    map[key].count += 1;
    total += p.revenue || 0;
  }
  const result = Object.values(map).sort((a, b) => b.value - a.value);
  result.forEach(item => { item.pct = total > 0 ? (item.value / total * 100) : 0; });
  return result;
}

// CSV Export function
function exportToCSV(projects) {
  if (!projects || projects.length === 0) {
    alert('No data to export');
    return;
  }

  const csvHeaders = [
    'Project ID', 'Customer Name', 'State', 'Postcode', 'Suburb', 'Location', 'System Type', 'Payment Method',
    'CRM Revenue', 'CRM Payment', 'Acct Revenue', 'Acct GP', 'Acct GP%',
    'CRM GP% Min', 'CRM GP% Max', 'CRM GP_max', 'CRM GP_min',
    'ocQuoteID', 'ocProjectID',
    'PV Size (kW)', 'Battery Size (kWh)',
    'Closing Date', 'Welcome Pack Date', 'Installation Date',
    'Sale→Job (days)', 'Job→WP (days)', 'Closing→WP (days)', 'Closing→Install (days)',
    'Customer hold (days)', 'Hold from', 'Hold to', 'Hold dates need checking'
  ];

  const csvRows = projects.map(p => {
    const crmGpMinPct = (p.gpMin && p.revenue) ? ((p.gpMin / p.revenue) * 100).toFixed(1) : '';
    const crmGpMaxPct = (p.gpMax && p.revenue) ? ((p.gpMax / p.revenue) * 100).toFixed(1) : '';

    return [
      p.projectId || '',
      p.customerName || '',
      p.state || '',
      p.postcode || '',
      p.suburb || '',
      p.locationClass || '',
      p.systemType || '',
      p.paymentMethod || '',
      p.revenue || 0,
      p.payment || 0,
      p.acctRevenue || 0,
      p.gp || 0,
      p.gpPct != null ? p.gpPct.toFixed(1) : '',
      crmGpMinPct,
      crmGpMaxPct,
      p.gpMax || 0,
      p.gpMin || 0,
      p.ocQuoteID || '',
      p.ocProjectID || '',
      p.pvSize || '',
      p.batterySize || '',
      p.closingDate ? dateFmt(p.closingDate) : '',
      p.welcomePackAt ? dateFmt(p.welcomePackAt) : '',
      p.installationAt ? dateFmt(p.installationAt) : '',
      p.daysToJob || '',
      p.jobToWelcome || '',
      p.daysToWelcome || '',
      p.daysToInstall || '',
      p.customerHoldDays || '',
      p.customerHoldFrom ? dateFmt(p.customerHoldFrom) : '',
      p.customerHoldTo ? dateFmt(p.customerHoldTo) : (p.customerHoldOpen ? 'still on hold' : ''),
      p.customerHoldSuspectReason || '',
    ];
  });

  // Escape CSV values
  const escapeCsv = (val) => {
    if (val == null) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const csvContent = [
    csvHeaders.map(escapeCsv).join(','),
    ...csvRows.map(row => row.map(escapeCsv).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `SH_Residential_${dayjs().format('YYYY-MM-DD')}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default function RetailFinance() {
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateField, setDateField] = useState('closingDate');
  const [location, setLocation] = useState('all');        // all | metro | rural
  const [exHold, setExHold] = useState(false);            // take customer holds out of cycle times
  const [filters, setFilters] = useState({ state: null, systemType: null, paymentMethod: null, search: '', gpFilter: null, gpFilterValue: null, stcStatus: null });
  const [chartFilter, setChartFilter] = useState(null); // { type, value, label }
  const [pageSize, setPageSize] = useState(20);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  // One category across every open project: clicking a cost line in any project
  // opens that same line's detail in all of them, which is the point of
  // expand-all - comparing one cost category across projects.
  const [detailCategory, setDetailCategory] = useState(null);

  const fetchData = useCallback(async (range, f, df) => {
    if (!range || !range[0] || !range[1]) return;
    setLoading(true);
    try {
      const params = {
        start: range[0].format('YYYY-MM-DD'),
        end: range[1].format('YYYY-MM-DD'),
        dateField: df,
      };
      if (f?.state) params.state = f.state;
      if (f?.systemType) params.systemType = f.systemType;
      if (f?.paymentMethod) params.paymentMethod = f.paymentMethod;
      if (f?.search) params.search = f.search;
      const res = await api.get('/api/retail-finance/summary', { params });
      setData(res.data.data);
    } catch { /* handled by interceptor */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(dateRange, filters, dateField); }, [dateRange, filters, dateField, fetchData]);

  const handleFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: typeof value === 'boolean' ? value : (value || null) }));
  };

  // Clear chart filter when backend data changes
  useEffect(() => { setChartFilter(null); }, [data]);

  const handleChartClick = (type, value, label) => {
    setChartFilter(prev => (prev?.type === type && prev?.value === value) ? null : { type, value, label });
  };

  const s = data?.stats || {};
  // Memoised because the fallback creates a new array every render, which would
  // re-run every derivation below it on every render.
  const rawProjects = useMemo(() => data?.projects || [], [data]);
  const opts = data?.filterOptions || {};

  /**
   * The two new filters, applied before anything else reads the list.
   *
   * Location is a row filter. Excluding customer holds is not — it changes the
   * durations themselves, so it swaps in the figures the backend already
   * computed with each hold clipped to the interval it actually overlapped.
   * Doing it here rather than at the fetch means both answers are one click
   * apart, and the KPI cards, the histograms, the table and the export all move
   * together because they all read this array.
   */
  const projects = useMemo(() => {
    let out = rawProjects;
    if (location !== 'all') out = out.filter((p) => p.locationClass === location);
    if (!exHold) return out;
    return out.map((p) => {
      if (!p.customerHoldDays) return p;
      // A hold whose own dates contradict each other cannot produce a duration
      // worth deducting, and a guess at what was meant would hide the record
      // from the people fixing it. The project stays in the list and keeps its
      // revenue; only its cycle times go, and every median, P90 and histogram
      // already skips a null.
      if (p.customerHoldSuspect) {
        return {
          ...p,
          daysToWelcome: null,
          daysToJob: null,
          jobToWelcome: null,
          daysToInstall: null,
        };
      }
      return {
        ...p,
        daysToWelcome: p.daysToWelcomeExHold,
        daysToJob: p.daysToJobExHold,
        jobToWelcome: p.jobToWelcomeExHold,
        daysToInstall: p.daysToInstallExHold,
      };
    });
  }, [rawProjects, location, exHold]);

  /** What the two filters are doing, so a number that moved can be explained. */
  const filterEffect = useMemo(() => {
    const held = rawProjects.filter((p) => p.customerHoldDays > 0);
    const heldHere = projects.filter((p) => p.customerHoldDays > 0);
    return {
      total: rawProjects.length,
      shown: projects.length,
      metro: rawProjects.filter((p) => p.locationClass === 'metro').length,
      rural: rawProjects.filter((p) => p.locationClass === 'rural').length,
      unknown: rawProjects.filter((p) => !p.locationClass).length,
      heldAll: held.length,
      heldShown: heldHere.length,
      heldDays: Math.round(heldHere.reduce((t, p) => t + (p.customerHoldDays || 0), 0)),
      stillHeld: heldHere.filter((p) => p.customerHoldOpen).length,
      suspect: heldHere.filter((p) => p.customerHoldSuspect).length,
      deducted: heldHere.filter((p) => !p.customerHoldSuspect).length,
      deductedDays: Math.round(heldHere
        .filter((p) => !p.customerHoldSuspect)
        .reduce((t, x) => t + (x.customerHoldDays || 0), 0)),
    };
  }, [rawProjects, projects]);

  /**
   * Everything the dropdowns narrow, before the chart selection is applied.
   *
   * The charts draw from this and the table draws from filteredProjects. If a
   * histogram drew from its own output, clicking one of its bars would leave
   * it showing that bar alone — the selection would destroy the context it was
   * made against. Drawing from here, the distribution holds still and the
   * chosen bucket is marked by dimming the others.
   */
  const chartSource = useMemo(() => {
    let result = projects;
    if (filters.gpFilter === 'lowGp') {
      result = result.filter(p => p.gp != null && p.gpMin != null && p.gp < p.gpMin);
    } else if (filters.gpFilter === 'pctLt' && filters.gpFilterValue != null) {
      result = result.filter(p => p.gpPct != null && p.gpPct < filters.gpFilterValue);
    } else if (filters.gpFilter === 'pctGt' && filters.gpFilterValue != null) {
      result = result.filter(p => p.gpPct != null && p.gpPct > filters.gpFilterValue);
    }
    if (filters.stcStatus === 'collected') result = result.filter(p => p.stcAllCollected);
    else if (filters.stcStatus === 'awaiting') result = result.filter(p => p.stcOutstanding || p.bstcOutstanding);
    else if (filters.stcStatus === 'subsidy') result = result.filter(p => p.stcHasSubsidy || p.subsidyApplicable);
    return result;
  }, [projects, filters.gpFilter, filters.gpFilterValue, filters.stcStatus]);

  // Client-side chart filter + lowGpOnly filter
  const filteredProjects = useMemo(() => {
    let result = projects;

    // Apply chart filter
    if (chartFilter) {
      const { type, value } = chartFilter;
      // A bucket label is "30-40", or "170+" for the open-ended last one.
      const bounds = (v) => (String(v).endsWith('+')
        ? [parseInt(v, 10), Infinity]
        : String(v).split('-').map(Number));
      switch (type) {
        case 'state': result = result.filter(p => (p.state || 'Unknown') === value); break;
        case 'systemType': result = result.filter(p => (p.systemType || 'Unknown') === value); break;
        case 'paymentMethod': result = result.filter(p => (p.paymentMethod || 'Unknown') === value); break;
        case 'saleToJob': {
          const [lo, hi] = bounds(value);
          result = result.filter(p => p.daysToJob != null && p.daysToJob >= lo && p.daysToJob < hi);
          break;
        }
        case 'jobToWP': {
          const [lo, hi] = bounds(value);
          result = result.filter(p => p.jobToWelcome != null && p.jobToWelcome >= lo && p.jobToWelcome < hi);
          break;
        }
        case 'wpAwaiting': {
          // Still waiting, and waiting this long so far. Measured to today, so
          // it has to be recomputed rather than read off the project.
          const [lo, hi] = bounds(value);
          const DAY = 1000 * 60 * 60 * 24;
          const nowMs = Date.now();
          result = result.filter((p) => {
            if (p.welcomePackAt || !p.closingDate) return false;
            const d = Math.round((nowMs - new Date(p.closingDate)) / DAY);
            return d >= lo && d < hi;
          });
          break;
        }
        case 'wpTaken': {
          const [lo, hi] = bounds(value);
          result = result.filter(p => p.daysToWelcome != null && p.daysToWelcome >= lo && p.daysToWelcome < hi);
          break;
        }
        default: break;
      }
    }

    // GP filter (dropdown): low-GP_min, pct under threshold, pct over threshold
    if (filters.gpFilter === 'lowGp') {
      result = result.filter(p => p.gp != null && p.gpMin != null && p.gp < p.gpMin);
    } else if (filters.gpFilter === 'pctLt' && filters.gpFilterValue != null) {
      result = result.filter(p => p.gpPct != null && p.gpPct < filters.gpFilterValue);
    } else if (filters.gpFilter === 'pctGt' && filters.gpFilterValue != null) {
      result = result.filter(p => p.gpPct != null && p.gpPct > filters.gpFilterValue);
    }

    /**
     * STC status, for projects that had an incentive invoiced at all.
     *
     * "Collected" means the money covers what was billed, not merely that
     * something arrived — receipts come as lump settlements naming the payer
     * rather than the incentive, so whether a particular component is in can
     * only be answered on the total. The two "only" options are about what was
     * billed, which the invoice does say per line.
     */
    if (filters.stcStatus === 'collected') {
      // Every certificate this project can claim has been collected — solar,
      // battery, or both, whichever of them apply.
      result = result.filter(p => p.stcAllCollected);
    } else if (filters.stcStatus === 'awaiting') {
      // The complement: at least one applicable certificate is still out.
      // Partly collected belongs here rather than in a category of its own.
      result = result.filter(p => p.stcOutstanding || p.bstcOutstanding);
    } else if (filters.stcStatus === 'subsidy') {
      result = result.filter(p => p.stcHasSubsidy || p.subsidyApplicable);
    }

    return result;
  }, [projects, chartFilter, filters.gpFilter, filters.gpFilterValue, filters.stcStatus]);

  // Recompute stats from filtered projects (or use server stats if nothing narrows them)
  //
  // Every client-side filter has to be listed here. The shortcut returns the
  // server's stats for the whole period, so a filter missing from this condition
  // narrows the table and the banner while leaving every scorecard showing the
  // unfiltered figures — which is exactly what location and exclude-hold did
  // when they were added: "49 of 127" above cards that all still read 127.
  const narrowed = !!chartFilter || !!filters.gpFilter || !!filters.stcStatus
    || location !== 'all' || exHold;

  const fs = useMemo(() => {
    if (!narrowed) return s;
    const fp = filteredProjects;
    const toJobDays = fp.map(p => p.daysToJob).filter(d => d != null).sort((a, b) => a - b);
    const jobToWpDays = fp.map(p => p.jobToWelcome).filter(d => d != null).sort((a, b) => a - b);
    const welcomeDays = fp.map(p => p.daysToWelcome).filter(d => d != null).sort((a, b) => a - b);
    const installDays = fp.map(p => p.daysToInstall).filter(d => d != null).sort((a, b) => a - b);
    // Install → STC collected. Negative lags exist in the CRM (collection dated
    // before install on a few records) — excluded, same as the server does.
    const stcLagDays = fp.map(p => p.stcLagDays).filter(d => d != null && d >= 0).sort((a, b) => a - b);
    const bstcLagDays = fp.map(p => p.bstcLagDays).filter(d => d != null && d >= 0).sort((a, b) => a - b);
    const mean = (arr) => arr.length ? arr.reduce((sum, v) => sum + v, 0) / arr.length : null;
    const totalGp = fp.reduce((sum, p) => sum + (p.gp || 0), 0);
    const totalAcctRevenue = fp.reduce((sum, p) => sum + (p.acctRevenue || 0), 0);
    const totalGpPct = totalAcctRevenue > 0 ? (totalGp / totalAcctRevenue) * 100 : 0;
    const totalStcVariation = fp.reduce((sum, p) => sum + (p.stcVariationExGst || 0), 0);
    const lowGpProjects = fp.filter(p => p.gp != null && p.gpMin != null && p.gp < p.gpMin);
    const gpMinValues = fp.filter(p => p.gpMin != null).map(p => p.gpMin);
    const gpMaxValues = fp.filter(p => p.gpMax != null).map(p => p.gpMax);
    const totalCrmGpMin = gpMinValues.reduce((sum, v) => sum + v, 0);
    const totalCrmGpMax = gpMaxValues.reduce((sum, v) => sum + v, 0);
    const totalRevenue = fp.reduce((sum, p) => sum + (p.revenue || 0), 0);
    const totalPvSize = fp.reduce((sum, p) => sum + (p.pvSize || 0), 0);
    const totalBatterySize = fp.reduce((sum, p) => sum + (p.batterySize || 0), 0);
    const upfrontCount = fp.filter(p => p.paymentMethod && p.paymentMethod.toLowerCase().includes('upfront')).length;
    return {
      projectCount: fp.length,
      totalRevenue,
      avgRevenue: fp.length > 0 ? totalRevenue / fp.length : 0,
      totalAcctRevenue,
      avgAcctRevenue: fp.length > 0 ? totalAcctRevenue / fp.length : 0,
      totalGp,
      totalGpPct,
      totalStcVariation,
      lowGpCount: lowGpProjects.length,
      lowGpPct: fp.length > 0 ? (lowGpProjects.length / fp.length) * 100 : 0,
      totalCrmGpMin,
      totalCrmGpMax,
      avgCrmGpMin: gpMinValues.length > 0 ? totalCrmGpMin / gpMinValues.length : 0,
      designedGpPctMin: totalRevenue > 0 ? (totalCrmGpMin / totalRevenue) * 100 : null,
      designedGpPctMax: totalRevenue > 0 ? (totalCrmGpMax / totalRevenue) * 100 : null,
      upfrontPct: fp.length > 0 ? (upfrontCount / fp.length) * 100 : 0,
      totalPvSize,
      avgPvSize: fp.length > 0 ? totalPvSize / fp.length : 0,
      totalBatterySize,
      avgBatterySize: fp.length > 0 ? totalBatterySize / fp.length : 0,
      welcomePackCount: fp.filter(p => p.welcomePackAt).length,
      installCount: fp.filter(p => p.installationAt).length,
      awaitingStcCount: fp.filter(p => (p.stcPlanned || 0) > 0 && (p.stcActual || 0) === 0).length,
      toJobDaysP90: percentile(toJobDays, 90),
      toJobDaysMedian: percentile(toJobDays, 50),
      jobToWelcomeP90: percentile(jobToWpDays, 90),
      jobToWelcomeMedian: percentile(jobToWpDays, 50),
      welcomeDaysP90: percentile(welcomeDays, 90),
      welcomeDaysMedian: percentile(welcomeDays, 50),
      closingToInstallP90: percentile(installDays, 90),
      closingToInstallMedian: percentile(installDays, 50),
      stcLagAvg: mean(stcLagDays),
      stcLagP90: percentile(stcLagDays, 90),
      stcLagCount: stcLagDays.length,
      bstcLagAvg: mean(bstcLagDays),
      bstcLagP90: percentile(bstcLagDays, 90),
      bstcLagCount: bstcLagDays.length,
    };
  }, [narrowed, filteredProjects, s]);
  const isSingleMonth = dateRange?.[0] && dateRange?.[1]
    && dateRange[0].year() === dateRange[1].year()
    && dateRange[0].month() === dateRange[1].month();

  // Pie chart data for single-month view
  const pieByState = useMemo(() => groupByField(projects, 'state'), [projects]);
  const pieBySystem = useMemo(() => groupByField(projects, 'systemType'), [projects]);
  const pieByPayment = useMemo(() => groupByField(projects, 'paymentMethod'), [projects]);

  // Duration distribution histograms
  const histToJob = useMemo(
    () => buildHistogram(chartSource.map(p => p.daysToJob).filter(d => d != null)), [chartSource],
  );
  const histJobToWP = useMemo(
    () => buildHistogram(chartSource.map(p => p.jobToWelcome).filter(d => d != null)), [chartSource],
  );

  /**
   * How long projects wait for a welcome pack, finished against unfinished.
   *
   * Two series over one axis: how long the projects still without a welcome
   * pack have been waiting so far, and how long the ones that got there took.
   * Read together they say whether the queue is longer than the history would
   * lead you to expect, which neither series says on its own — a backlog can
   * look healthy right up until you notice nothing in it has moved.
   *
   * The waiting side is measured to today, so it grows on its own. It is
   * necessarily an underestimate of what those projects will finally take.
   */
  const histWelcomePack = useMemo(() => {
    const DAY = 1000 * 60 * 60 * 24;
    const nowMs = Date.now();
    const awaiting = chartSource
      .filter((p) => !p.welcomePackAt && p.closingDate)
      .map((p) => Math.round((nowMs - new Date(p.closingDate)) / DAY))
      .filter((d) => d >= 0);
    const completed = chartSource
      .filter((p) => p.daysToWelcome != null)
      .map((p) => p.daysToWelcome);
    return { rows: buildPairedHistogram(awaiting, completed), awaiting: awaiting.length, completed: completed.length };
  }, [chartSource]);

  // Multi-month: line chart — monthly revenue totals grouped by date field month
  const lineChartData = useMemo(() => {
    if (isSingleMonth) return [];
    const dateKey = dateField === 'welcomePackAt' ? 'welcomePackAt' : 'closingDate';
    const monthMap = {};
    // filteredProjects, not projects: every other figure on this page reflects
    // the state, system, payment, GP and STC filters, and a revenue line that
    // quietly ignored them disagreed with the table underneath it.
    for (const p of chartSource) {
      if (!p[dateKey]) continue;
      const d = new Date(p[dateKey]);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthMap[key]) monthMap[key] = { month: key, Revenue: 0, Projects: 0 };
      monthMap[key].Revenue += p.revenue || 0;
      monthMap[key].Projects += 1;
    }
    return Object.values(monthMap)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(m => ({
        ...m,
        Revenue: Math.round(m.Revenue),
        label: new Date(m.month + '-15').toLocaleDateString('en-AU', { month: 'short', year: '2-digit' }),
      }));
  }, [chartSource, isSingleMonth, dateField]);

  // Whether every filtered project is already open, which flips the header
  // toggle between expand-all and collapse-all
  const allExpanded = filteredProjects.length > 0
    && expandedRowKeys.length >= filteredProjects.length;

  // Expandable row: Project Cost Breakdown (Items / Designed / Actual) + Detail panel
  const expandedRowRender = (record) => {
    const design = record.designBreakdown;
    const txns = record.xeroBreakdown || [];
    const hasTxns = txns.length > 0;

    // --- Designed column (from PPA quote + CRM) ---
    const isPPA = record.paymentMethod && record.paymentMethod.toLowerCase().includes('ppa');
    const dRebate = design ? ((design.stcRebate || 0) + (design.batteryStc || 0) + (design.govRebate || 0)) : null;
    // PPA: income = systemCost + rebates; Upfront/BNPL: income = GST*10 (CRM revenue)
    let dIncome, dPayment;
    if (!design) {
      dIncome = null; dPayment = null;
    } else if (isPPA) {
      dPayment = design.systemCost || 0;
      dIncome = dPayment + dRebate;
    } else {
      dIncome = record.revenue || 0;       // totalGst * 10
      dPayment = dIncome - dRebate;
    }
    const dEquipment = design ? ((design.material || 0) + (design.extraMaterial || 0) + (design.bos || 0)) : null;
    const dInstallation = design ? ((design.installation || 0) + (design.adder || 0)) : null;
    const dOthers = design ? ((design.buffer || 0) + (design.bnplSurcharge || 0)) : null;
    const dExpenses = design ? (dEquipment + dInstallation + dOthers) : null;
    const dGP = design ? (dIncome - dExpenses) : null;
    const dGPPct = design && dIncome > 0 ? (dGP / dIncome) * 100 : null;

    // --- Classify Xero transactions by category ---
    const txnCat = { income: [], equipment: [], installation: [], others: [] };
    let aIncome = 0, aEquipment = 0, aInstallation = 0, aOthers = 0;
    for (const t of txns) {
      const acct = (t.account || '').toLowerCase();
      const aType = (t.accountType || '').toLowerCase();
      if (aType.includes('revenue') || aType.includes('income') || acct.includes('package income')) {
        txnCat.income.push(t);
        aIncome += (t.credit || 0) - (t.debit || 0);
      } else if (acct.includes('panel') || acct.includes('battery') || acct.includes('inverter') || acct.includes('equipment') || acct.includes('bos')) {
        txnCat.equipment.push(t);
        aEquipment += (t.debit || 0) - (t.credit || 0);
      } else if (acct.includes('installer')) {
        txnCat.installation.push(t);
        aInstallation += (t.debit || 0) - (t.credit || 0);
      } else {
        txnCat.others.push(t);
        aOthers += (t.debit || 0) - (t.credit || 0);
      }
    }
    const aExpenses = aEquipment + aInstallation + aOthers;

    // STC clearing (FY26 sheet) — planned (Debit) vs actual (Credit) received.
    // stcVariation is added to Income as a separate row so the project's true
    // realised income reflects the Formbay payout shortfall/surplus.
    const stcPlanned   = record.stcPlanned   || 0;
    const stcActual    = record.stcActual    || 0;
    const stcVariation = record.stcVariation || 0;           // clearing account, GST-inclusive
    const stcVariationExGst = record.stcVariationExGst || 0;  // what Income and GP use
    const dIncomeTotal = (dIncome || 0); // designed: no variation by definition
    const aIncomeTotal = (aIncome || 0) + stcVariationExGst;
    // Use total income (incl. STC clearing variation) for actual GP & GP%
    const aGP = aIncomeTotal - aExpenses;
    const aGPPct = aIncomeTotal > 0 ? (aGP / aIncomeTotal) * 100 : null;

    // --- Detail data per category ---
    const detailMap = {
      packageIncome: {
        title: 'Package Income',
        designed: design ? [
          { label: isPPA ? 'System Cost' : 'Customer Payment (ex-GST)', value: dPayment },
          { label: 'STC Rebate', value: design.stcRebate },
          { label: 'Battery STC', value: design.batteryStc },
          { label: 'Govt Rebate', value: design.govRebate },
        ] : [],
        txns: txnCat.income,
        isIncome: true,
      },
      stcVariation: {
        title: 'Actual STC (clearing variation)',
        isIncome: true,
        designed: [
          { label: 'Invoiced STC + bSTC (Debit)', value: record.recognitionDebit || (stcPlanned + (record.stcAdjustment || 0)) },
          ...(record.stcAdjustment ? [
            { label: 'Less adjustment journals (ADJ)', value: -(record.stcAdjustment || 0) },
          ] : []),
          { label: 'Planned STC + bSTC, revised', value: stcPlanned },
        ],
        actuals: [
          { label: 'Actual STC received (Credit)', value: stcActual },
          { label: 'Variation (Received − Planned)', value: stcVariation },
          { label: 'Variation ex-GST (÷ 1.1) — added to Income', value: stcVariationExGst },
        ],
        txns: [],
        sourceNote: 'Source: Xero clearing account "Raw Data - STC FY 26"',
        // Second, independent view: what the CRM itself recorded as banked.
        // Solar and battery STCs settle separately, so each reports its own
        // amount and date — a missing date means that half is still owed.
        crmLines: buildCrmStcLines(record),
      },
      equipment: {
        title: 'Equipment',
        designed: design ? [
          { label: 'Material', value: design.material },
          { label: 'Extra Material', value: design.extraMaterial },
          { label: 'BOS', value: design.bos },
        ] : [],
        txns: txnCat.equipment,
      },
      installation: {
        title: 'Installation',
        designed: design ? [
          { label: 'Installation', value: design.installation },
          { label: 'Adder', value: design.adder },
        ] : [],
        txns: txnCat.installation,
      },
      others: {
        title: 'Others',
        designed: design ? [
          { label: 'Buffer', value: design.buffer },
          ...(design.bnplSurcharge != null
            ? [{ label: 'BNPL Surcharge', value: design.bnplSurcharge }]
            : []),
        ] : [],
        txns: txnCat.others,
      },
    };

    // Group transactions by account for detail display
    const groupByAccount = (list) => {
      const map = {};
      for (const t of (list || [])) {
        const key = t.account || 'Unknown';
        if (!map[key]) map[key] = { account: key, debit: 0, credit: 0, count: 0 };
        map[key].debit += t.debit || 0;
        map[key].credit += t.credit || 0;
        map[key].count++;
      }
      return Object.values(map).sort((a, b) => (b.debit + b.credit) - (a.debit + a.credit));
    };

    const fmtVal = (v) => {
      if (v == null) return '-';
      const neg = v < 0;
      const abs = Math.abs(v);
      const str = `$${abs.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      return neg ? `-${str}` : str;
    };

    const cs = { padding: '4px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 12 };
    const ls = { padding: '4px 12px', fontSize: 12 };
    const ts = { ...cs, fontWeight: 700, borderTop: '1px solid #d9d9d9' };
    const tls = { ...ls, fontWeight: 700, borderTop: '1px solid #d9d9d9' };
    const gs = { ...cs, fontWeight: 700, borderTop: '2px solid #999' };
    const gls = { ...ls, fontWeight: 700, borderTop: '2px solid #999' };
    const ss = { padding: '6px 12px', fontWeight: 700, fontSize: 12, backgroundColor: '#fafafa' };
    const gpColor = (v) => v != null ? (v >= 0 ? '#05A677' : '#FA5252') : undefined;

    const activeCat = detailCategory;
    const clickable = (cat) => ({
      cursor: 'pointer',
      backgroundColor: activeCat === cat ? '#e6f4ff' : undefined,
    });
    const onCatClick = (cat) => setDetailCategory((prev) => (prev === cat ? null : cat));

    // --- Detail panel ---
    const detail = activeCat ? detailMap[activeCat] : null;
    const detailAccounts = detail ? groupByAccount(detail.txns) : [];

    /**
     * Actuals exist here, but the row above shows nothing for them.
     *
     * Acct Rev and Acct GP are only counted once the welcome pack has gone
     * out — that is the point the project is treated as earned. This panel has
     * no such gate: it shows whatever Xero holds against the project. So a
     * project invoiced but not yet handed over reads as real money here and as
     * zero one line up, which looks like a fault in the page rather than the
     * rule it actually is.
     *
     * Saying so is the whole fix. The figures are not changed and the totals
     * stay exactly as reported.
     */
    const notCountedYet = hasTxns && !record.welcomePackAt;

    return (
      <Row gutter={16} style={{ padding: '4px 0' }}>
        <Col xs={24} md={12} lg={10}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Project Cost Breakdown</div>
          {notCountedYet && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 8 }}
              message="Actuals not counted yet"
              description={(
                <span style={{ fontSize: 12 }}>
                  Xero holds these figures against the project, but no customer welcome pack has
                  been issued, so they are not yet counted towards Acct Rev or Acct GP — which is
                  why the row above reads $0. They will appear there once the welcome pack goes out.
                </span>
              )}
            />
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #d9d9d9', backgroundColor: '#fafafa' }}>
                <th style={{ ...ls, fontWeight: 700 }}>Items</th>
                <th style={{ ...cs, fontWeight: 700 }}>Designed</th>
                <th style={{ ...cs, fontWeight: 700 }}>Actual</th>
              </tr>
            </thead>
            <tbody>
              <tr><td colSpan={3} style={ss}>Income</td></tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0', ...clickable('packageIncome') }} onClick={() => onCatClick('packageIncome')}>
                <td style={ls}>1. Package Income</td>
                <td style={cs}>{design ? fmtVal(dIncome) : '-'}</td>
                <td style={cs}>{hasTxns ? fmtVal(aIncome) : '-'}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0', ...clickable('stcVariation') }} onClick={() => onCatClick('stcVariation')}>
                <td style={ls}>
                  2. Actual STC (variation vs invoice)
                  {/* What the clearing account was actually billed for. The
                      components come from the invoice lines; which of them a
                      receipt settles is not knowable, so only the billing is
                      named here. */}
                  {(record.stcHasStc || record.stcHasBstc || record.stcHasSubsidy) && (
                    <div style={{ marginTop: 3 }}>
                      {record.stcHasStc && (
                        <Tag color="blue" style={{ marginRight: 4, fontSize: 10 }}>
                          STC {money2(record.stcBilledStc)}
                        </Tag>
                      )}
                      {record.stcHasBstc && (
                        <Tag color="purple" style={{ marginRight: 4, fontSize: 10 }}>
                          BSTC {money2(record.stcBilledBstc)}
                        </Tag>
                      )}
                      {(record.stcHasSubsidy || record.subsidyApplicable) && (
                        <Tag
                          color={record.subsidyCollected ? 'green' : 'orange'}
                          style={{ marginRight: 4, fontSize: 10 }}
                        >
                          {record.subsidyScheme === 'VIC' ? 'Vic rebate ' : 'Subsidy '}
                          {money2(record.stcBilledSubsidy || record.subsidyAmount)}
                          {record.subsidyApplicable
                            ? (record.subsidyCollected ? ' ✓' : ' pending')
                            : ' no CRM record'}
                        </Tag>
                      )}
                    </div>
                  )}
                </td>
                <td style={cs}>-</td>
                <td style={{ ...cs, color: stcVariationExGst < 0 ? '#cf1322' : stcVariationExGst > 0 ? '#05A677' : undefined }}>
                  {stcVariation || stcPlanned || stcActual ? fmtVal(stcVariationExGst) : '-'}
                </td>
              </tr>
              <tr>
                <td style={tls}>Total Income</td>
                <td style={ts}>{design ? fmtVal(dIncomeTotal) : '-'}</td>
                <td style={ts}>{hasTxns ? fmtVal(aIncomeTotal) : '-'}</td>
              </tr>
              <tr><td colSpan={3} style={{ ...ss, paddingTop: 10 }}>Expenses</td></tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0', ...clickable('equipment') }} onClick={() => onCatClick('equipment')}>
                <td style={ls}>1. Equipment</td>
                <td style={cs}>{design ? fmtVal(dEquipment) : '-'}</td>
                <td style={cs}>{hasTxns ? fmtVal(aEquipment) : '-'}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0', ...clickable('installation') }} onClick={() => onCatClick('installation')}>
                <td style={ls}>2. Installation</td>
                <td style={cs}>{design ? fmtVal(dInstallation) : '-'}</td>
                <td style={cs}>{hasTxns ? fmtVal(aInstallation) : '-'}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0', ...clickable('others') }} onClick={() => onCatClick('others')}>
                <td style={ls}>3. Others</td>
                <td style={cs}>{design ? fmtVal(dOthers) : '-'}</td>
                <td style={cs}>{hasTxns ? fmtVal(aOthers) : '-'}</td>
              </tr>
              <tr>
                <td style={tls}>Total Expenses</td>
                <td style={ts}>{design ? fmtVal(dExpenses) : '-'}</td>
                <td style={ts}>{hasTxns ? fmtVal(aExpenses) : '-'}</td>
              </tr>
              <tr>
                <td style={gls}>GP</td>
                <td style={{ ...gs, color: gpColor(dGP) }}>{design ? fmtVal(dGP) : '-'}</td>
                <td style={{ ...gs, color: gpColor(aGP) }}>{hasTxns ? fmtVal(aGP) : '-'}</td>
              </tr>
              <tr>
                <td style={{ ...ls, fontWeight: 700 }}>GP%</td>
                <td style={{ ...cs, fontWeight: 700, color: gpColor(dGPPct) }}>{dGPPct != null ? `${dGPPct.toFixed(1)}%` : '-'}</td>
                <td style={{ ...cs, fontWeight: 700, color: gpColor(aGPPct) }}>{aGPPct != null ? `${aGPPct.toFixed(1)}%` : '-'}</td>
              </tr>
            </tbody>
          </table>
        </Col>
        {detail && (
          <Col xs={24} md={12} lg={14}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
              {detail.title} &mdash; Detail
              <span style={{ color: '#999', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
                {record.projectId}{record.customerName ? ` · ${record.customerName}` : ''}
              </span>
            </div>
            <Row gutter={16}>
              <Col xs={24} lg={12}>
                <div style={{ fontWeight: 600, fontSize: 12, color: '#54A2CC', marginBottom: 4 }}>Designed Budget</div>
                {detail.designed.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <tbody>
                      {detail.designed.map((item) => (
                        <tr key={item.label} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '3px 8px 3px 0', color: '#666' }}>{item.label}</td>
                          <td style={{ padding: '3px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtVal(item.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <span style={{ color: '#999', fontSize: 12 }}>No quote data</span>
                )}
              </Col>
              <Col xs={24} lg={12}>
                <div style={{ fontWeight: 600, fontSize: 12, color: '#05A677', marginBottom: 4 }}>
                  {detail.isIncome ? 'Actual Income' : 'Actual Expense'}
                </div>
                {detail.actuals && detail.actuals.length > 0 ? (
                  <>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <tbody>
                        {detail.actuals.map((item) => (
                          <tr key={item.label} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '3px 8px 3px 0', color: '#666' }}>{item.label}</td>
                            <td style={{ padding: '3px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtVal(item.value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {detail.sourceNote && (
                      <div style={{ color: '#999', fontSize: 11, marginTop: 6 }}>{detail.sourceNote}</div>
                    )}
                    {detail.crmLines && detail.crmLines.length > 0 && (
                      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed #e8e8e8' }}>
                        {detail.crmLines.map((l, i) => (
                          <div key={i} style={{
                            fontSize: 12,
                            marginBottom: 2,
                            fontWeight: l.strong ? 600 : 400,
                            fontStyle: l.na ? 'italic' : 'normal',
                            // outstanding → red (actionable); not applicable → muted
                            color: l.pending ? '#FA5252' : l.na ? '#bbb' : (l.strong ? '#333' : '#666'),
                          }}>
                            {l.text}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : detail.note ? (
                  <span style={{ color: '#999', fontSize: 12 }}>{detail.note}</span>
                ) : detailAccounts.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <tbody>
                      {detailAccounts.map((a) => {
                        const net = detail.isIncome ? (a.credit - a.debit) : (a.debit - a.credit);
                        return (
                          <tr key={a.account} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '3px 8px 3px 0', color: '#666' }}>{a.account} <span style={{ color: '#bbb' }}>({a.count})</span></td>
                            <td style={{ padding: '3px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtVal(net)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <span style={{ color: '#999', fontSize: 12 }}>No transactions</span>
                )}
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    );
  };

  const tableTitleText = chartFilter
    // Denominator is the location-filtered set, not the server's whole-period
    // count: "12 of 49 rural" is a comparison, "12 of 127" is two unrelated
    // numbers side by side.
    ? `${filteredProjects.length} of ${projects.length} Projects — ${chartFilter.label}`
    : `${projects.length}${location !== 'all' ? ` ${location}` : ''} Sales, ${fs.welcomePackCount || 0} Welcome Pack Sent, ${fs.awaitingStcCount || 0} Awaiting STC Collection`;

  const tableTitle = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
      <span>{tableTitleText}</span>
      <Button
        type="primary"
        icon={<DownloadOutlined />}
        onClick={() => exportToCSV(filteredProjects)}
      >
        Export CSV
      </Button>
    </div>
  );

  const renderPie = (data, title, filterType) => (
    <Col xs={24} md={8}>
      <Card size="small" title={title} style={{ marginTop: 16 }}>
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={80}
              label={({ name, count, pct, value }) => `${name} (${count}) ${fmt(value)} ${pct.toFixed(0)}%`}
              fontSize={10}
              onClick={(entry) => handleChartClick(filterType, entry.name, `${title.replace('Revenue by ', '')}: ${entry.name}`)}
              style={{ cursor: 'pointer' }}
            >
              {data.map((entry, i) => (
                <Cell
                  key={i}
                  fill={PIE_COLORS[i % PIE_COLORS.length]}
                  opacity={chartFilter?.type === filterType && chartFilter?.value !== entry.name ? 0.3 : 1}
                />
              ))}
            </Pie>
            <Tooltip formatter={(v) => fmt(v)} />
          </PieChart>
        </ResponsiveContainer>
      </Card>
    </Col>
  );

  return (
    <PageLayout>
      <div className="page-header">
        <h1>Shinehub Residential Projects</h1>
        <p>Won projects by closing date — CRM + Xero accounts</p>
      </div>
      {/*
        * Two questions about the same jobs, which is why they are tabs rather
        * than two more cards on one page.
        *
        * Snapshot asks what delivered work earned and reconciles to the P&L.
        * Current Pipeline asks what undelivered work is owed and reconciles to
        * the invoice book. They share no filter and no date basis — the first
        * is cohorted on a date the reader picks, the second is always "as at
        * yesterday" — so putting them side by side would mean one toolbar
        * governing half a page, which is worse than a tab.
        */}
      <Tabs
        defaultActiveKey="snapshot"
        destroyInactiveTabPane={false}
        items={[{
          key: 'snapshot',
          label: 'Snapshot',
          children: (
            <>
      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <Space wrap size="middle" align="center">
          <CalendarOutlined style={{ color: '#54A2CC' }} />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            format="DD MMM YYYY"
            size="small"
            allowClear={false}
            presets={DATE_PRESETS}
          />
          <Radio.Group
            value={dateField}
            onChange={(e) => setDateField(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="closingDate">Closing Date</Radio.Button>
            <Radio.Button value="welcomePackAt">Welcome Pack Date</Radio.Button>
          </Radio.Group>

          <AntTooltip
            title={
              'By postcode. Construction have ruled on 54 postcodes and those rulings decide it; '
              + 'everything else follows the greater capital city areas. So Newcastle and Lake '
              + 'Macquarie, the Central Coast, Geelong, Mount Gambier, Port Pirie and Port Augusta '
              + 'are rural, while the Illawarra from Helensburgh through Wollongong to Kiama, '
              + 'Victor Harbor, Goolwa and the lower Barossa are metro. Mount Barker, Strathalbyn, '
              + 'Murray Bridge and Nuriootpa came back rural.'
            }
          >
            <Segmented
              size="small"
              value={location}
              onChange={setLocation}
              options={[
                { label: `All (${filterEffect.total})`, value: 'all' },
                { label: `Metro (${filterEffect.metro})`, value: 'metro', disabled: filterEffect.metro === 0 },
                { label: `Rural (${filterEffect.rural})`, value: 'rural', disabled: filterEffect.rural === 0 },
              ]}
            />
          </AntTooltip>

          <AntTooltip
            title={
              (filterEffect.heldShown === 0
                ? 'Nothing closing in this date range was put on hold at the customer\u2019s request, so '
                  + 'there is nothing to take out. Customer holds are uncommon \u2014 123 across four '
                  + 'years, of which 76 have dates worth trusting \u2014 so a single month will often have '
                  + 'none, and a median will rarely move even when it does. '
                : '')
              + 'Takes the time a job spent on hold at the customer\u2019s request out of every cycle '
              + 'time, medians and P90s included. The window is bounded by the job being created and '
              + 'by the installation happening \u2014 an installation hold cannot delay the creation of '
              + 'the job, and an install that went ahead ended the hold whatever the flag still says. '
              + 'Only the part falling inside an interval is removed. Stock and installer holds are '
              + 'our own delay and stay in.'
            }
          >
            <Space size={6}>
              <Switch
                size="small"
                checked={exHold}
                onChange={setExHold}
                // Nothing to take out is worth saying rather than leaving the
                // switch to move and change no number, which reads as broken.
                disabled={filterEffect.heldShown === 0}
              />
              <span style={{ fontSize: 13, color: filterEffect.heldShown === 0 ? '#8c8c8c' : undefined }}>
                Exclude customer hold
              </span>
              <Tag
                color={filterEffect.heldShown === 0 ? 'default' : (exHold ? 'blue' : 'default')}
                style={{ margin: 0 }}
              >
                {filterEffect.heldShown === 0 ? 'none in range' : `${filterEffect.heldShown} held`}
              </Tag>
            </Space>
          </AntTooltip>
        </Space>
      </Card>

      {(location !== 'all' || exHold) && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={(
            <span style={{ fontSize: 12 }}>
              {location !== 'all' && (
                <>
                  Showing <b>{location}</b> only — {filterEffect.shown} of {filterEffect.total} projects
                  {filterEffect.unknown > 0 && `, ${filterEffect.unknown} with no usable postcode left out`}
                  {filterEffect.shown === filterEffect.total
                    && ', so every project in this range is already ' + location}
                  .{' '}
                </>
              )}
              {exHold && (
                <>
                  Cycle times exclude <b>{filterEffect.deductedDays} days</b> of customer hold across{' '}
                  {filterEffect.deducted} project{filterEffect.deducted === 1 ? '' : 's'}
                  {filterEffect.stillHeld > 0 && ` (${filterEffect.stillHeld} still on hold, counted to today)`}
                  . Only holds typed Customer are deducted — stock, installer and construction holds
                  are our own delay.
                  {filterEffect.suspect > 0 && (
                    <>
                      {' '}
                      <b>{filterEffect.suspect} project{filterEffect.suspect === 1 ? '' : 's'}</b> left
                      out of the cycle-time figures altogether: their hold dates contradict each other,
                      so no duration can be deducted. Filter the Cust. hold column by &ldquo;Dates need
                      checking&rdquo; to send them back to construction.
                    </>
                  )}
                </>
              )}
            </span>
          )}
        />
      )}
      <Spin spinning={loading}>
        {/* KPI Cards — Row 1: volume + revenue.
            5 across doesn't divide the 24-column grid, so these rows size by
            flex-basis with a minWidth that lets them wrap on narrow screens. */}
        <Row gutter={[16, 16]}>
          <Col flex="1 1 20%" style={{ minWidth: 180 }}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title="Projects"
                value={fs.projectCount || 0}
                prefix={<ProjectOutlined style={{ color: '#54A2CC' }} />}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Upfront: {fs.upfrontPct != null ? `${fs.upfrontPct.toFixed(1)}%` : '-'}
              </div>
            </Card>
          </Col>
          <Col flex="1 1 20%" style={{ minWidth: 180 }}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title="Total PV (kW)"
                value={fs.totalPvSize ? fs.totalPvSize.toFixed(1) : 0}
                prefix={<ThunderboltOutlined style={{ color: '#f5b759' }} />}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Avg: {fs.avgPvSize ? `${fs.avgPvSize.toFixed(2)} kW` : '-'}
              </div>
            </Card>
          </Col>
          <Col flex="1 1 20%" style={{ minWidth: 180 }}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title="Total Battery (kWh)"
                value={fs.totalBatterySize ? fs.totalBatterySize.toFixed(1) : 0}
                prefix={<DatabaseOutlined style={{ color: '#845EC2' }} />}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Avg: {fs.avgBatterySize ? `${fs.avgBatterySize.toFixed(2)} kWh` : '-'}
              </div>
            </Card>
          </Col>
          <Col flex="1 1 20%" style={{ minWidth: 180 }}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title="CRM Revenue"
                value={fs.totalRevenue}
                formatter={(v) => fmt(v)}
                prefix={<DollarOutlined style={{ color: '#05A677' }} />}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Avg: {fs.avgRevenue ? fmt(fs.avgRevenue) : '-'}
              </div>
            </Card>
          </Col>
          <Col flex="1 1 20%" style={{ minWidth: 180 }}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title="Actual Revenue"
                value={fs.totalAcctRevenue}
                formatter={(v) => fmt(v)}
                prefix={<DollarOutlined style={{ color: '#54A2CC' }} />}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Avg: {fs.avgAcctRevenue ? fmt(fs.avgAcctRevenue) : '-'}
              </div>
            </Card>
          </Col>
        </Row>

        {/* KPI Cards — Row 2: cycle-time P90s.
            The STC pair shows n because CRM settlement coverage is partial, so
            they describe the settled subset rather than the whole cohort. */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col flex="1 1 20%" style={{ minWidth: 180 }}>
            <Card style={{ minHeight: 120 }}>
              <Statistic title="Sale → Job P90" value={fs.toJobDaysP90 ?? '-'} suffix={fs.toJobDaysP90 != null ? 'd' : ''} prefix={<ClockCircleOutlined style={{ color: '#54A2CC' }} />} />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>Median: {fs.toJobDaysMedian != null ? `${fs.toJobDaysMedian}d` : '-'}</div>
            </Card>
          </Col>
          <Col flex="1 1 20%" style={{ minWidth: 180 }}>
            <Card style={{ minHeight: 120 }}>
              <Statistic title="Job → WP P90" value={fs.jobToWelcomeP90 ?? '-'} suffix={fs.jobToWelcomeP90 != null ? 'd' : ''} prefix={<ClockCircleOutlined style={{ color: '#f5b759' }} />} />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>Median: {fs.jobToWelcomeMedian != null ? `${fs.jobToWelcomeMedian}d` : '-'}</div>
            </Card>
          </Col>
          <Col flex="1 1 20%" style={{ minWidth: 180 }}>
            <Card style={{ minHeight: 120 }}>
              <Statistic title="Closing → Install P90" value={fs.closingToInstallP90 ?? '-'} suffix={fs.closingToInstallP90 != null ? 'd' : ''} prefix={<ClockCircleOutlined style={{ color: '#FA5252' }} />} />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>Median: {fs.closingToInstallMedian != null ? `${fs.closingToInstallMedian}d` : '-'}</div>
            </Card>
          </Col>
          <Col flex="1 1 20%" style={{ minWidth: 180 }}>
            <Card style={{ minHeight: 120 }}>
              <Statistic title="Install → STC P90" value={fs.stcLagP90 ?? '-'} suffix={fs.stcLagP90 != null ? 'd' : ''} prefix={<ClockCircleOutlined style={{ color: '#05A677' }} />} />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Avg: {fs.stcLagAvg != null ? `${fs.stcLagAvg.toFixed(1)}d` : '-'}
                {fs.stcLagCount ? ` · n=${fs.stcLagCount}` : ''}
              </div>
            </Card>
          </Col>
          <Col flex="1 1 20%" style={{ minWidth: 180 }}>
            <Card style={{ minHeight: 120 }}>
              <Statistic title="Install → bSTC P90" value={fs.bstcLagP90 ?? '-'} suffix={fs.bstcLagP90 != null ? 'd' : ''} prefix={<ClockCircleOutlined style={{ color: '#845EC2' }} />} />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Avg: {fs.bstcLagAvg != null ? `${fs.bstcLagAvg.toFixed(1)}d` : '-'}
                {fs.bstcLagCount ? ` · n=${fs.bstcLagCount}` : ''}
              </div>
            </Card>
          </Col>
        </Row>

        {/* KPI Cards — Row 3: GP metrics + STC variation */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col flex="1 1 25%" style={{ minWidth: 200 }}>
            <Card size="small" style={{ minHeight: 100 }}>
              <Statistic
                title="Total CRM GP_min"
                value={fs.totalCrmGpMin}
                formatter={(v) => fmt(v)}
                valueStyle={{ fontSize: 22 }}
                prefix={<DollarOutlined style={{ color: '#05A677' }} />}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Avg: {fs.avgCrmGpMin ? fmt(fs.avgCrmGpMin) : '-'}
              </div>
            </Card>
          </Col>
          <Col flex="1 1 25%" style={{ minWidth: 200 }}>
            <Card size="small" style={{ minHeight: 100 }}>
              <Statistic
                title="Total Acct GP"
                value={fs.totalGp}
                formatter={(v) => fmt(v)}
                valueStyle={{ fontSize: 22, color: fs.totalGp >= 0 ? '#05A677' : '#FA5252' }}
                prefix={<RiseOutlined style={{ color: '#54A2CC' }} />}
              />
              <div style={{ fontSize: 12, color: '#FA5252', marginTop: 4 }}>
                {fs.lowGpCount ? `${fs.lowGpPct.toFixed(1)}% (${fs.lowGpCount} projects) GP < GP_min` : '-'}
              </div>
            </Card>
          </Col>
          <Col flex="1 1 25%" style={{ minWidth: 200 }}>
            <Card size="small" style={{ minHeight: 100 }}>
              <Statistic
                title="Total Acct GP%"
                value={fs.totalGpPct != null ? fs.totalGpPct.toFixed(1) : 0}
                suffix="%"
                valueStyle={{ fontSize: 22, color: fs.totalGpPct >= 0 ? '#05A677' : '#FA5252' }}
                prefix={<RiseOutlined style={{ color: '#54A2CC' }} />}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Designed: {fs.designedGpPctMin != null && fs.designedGpPctMax != null
                  ? `${fs.designedGpPctMin.toFixed(1)}% ~ ${fs.designedGpPctMax.toFixed(1)}%`
                  : '-'}
              </div>
            </Card>
          </Col>
          <Col flex="1 1 25%" style={{ minWidth: 200 }}>
            <Card size="small" style={{ minHeight: 100 }}>
              <Statistic
                title="Total STC Variation"
                value={fs.totalStcVariation || 0}
                formatter={(v) => fmt(v)}
                valueStyle={{ fontSize: 22, color: (fs.totalStcVariation || 0) >= 0 ? '#05A677' : '#FA5252' }}
                prefix={<DollarOutlined style={{ color: '#f5b759' }} />}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Actual STC − Planned (clearing)
              </div>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card style={{ marginTop: 16 }} size="small">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <Space wrap size="middle">
              <Input
                placeholder="Search name or ID"
                allowClear
                prefix={<SearchOutlined />}
                style={{ width: 170 }}
                value={filters.search}
                onChange={(e) => handleFilter('search', e.target.value)}
              />
              <Select
                placeholder="State"
                allowClear
                style={{ width: 100 }}
                value={filters.state}
                onChange={(v) => handleFilter('state', v)}
                options={opts.states?.map(s => ({ label: s, value: s })) || []}
              />
              <Select
                placeholder="System Type"
                allowClear
                style={{ width: 150 }}
                value={filters.systemType}
                onChange={(v) => handleFilter('systemType', v)}
                options={opts.systemTypes?.map(s => ({ label: s, value: s })) || []}
              />
              <Select
                placeholder="Payment Method"
                allowClear
                style={{ width: 150 }}
                value={filters.paymentMethod}
                onChange={(v) => handleFilter('paymentMethod', v)}
                options={opts.paymentMethods?.map(s => ({ label: s, value: s })) || []}
              />
              <Select
                placeholder="GP filter"
                popupMatchSelectWidth={false}
                allowClear
                style={{ width: 160 }}
                value={filters.gpFilter}
                onChange={(v) => {
                  setFilters(prev => ({ ...prev, gpFilter: v || null, gpFilterValue: v === 'lowGp' || !v ? null : prev.gpFilterValue }));
                }}
                options={[
                  { label: 'Acct GP < CRM GP_min', value: 'lowGp' },
                  {
                    label: filters.gpFilter === 'pctLt' && filters.gpFilterValue != null
                      ? `Acct GP% < ${filters.gpFilterValue}%`
                      : 'Acct GP% <',
                    value: 'pctLt',
                  },
                  {
                    label: filters.gpFilter === 'pctGt' && filters.gpFilterValue != null
                      ? `Acct GP% > ${filters.gpFilterValue}%`
                      : 'Acct GP% >',
                    value: 'pctGt',
                  },
                ]}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    {(filters.gpFilter === 'pctLt' || filters.gpFilter === 'pctGt') && (
                      <div style={{ padding: 8, borderTop: '1px solid #f0f0f0' }} onMouseDown={(e) => e.preventDefault()}>
                        <InputNumber
                          placeholder="Threshold"
                          style={{ width: '100%' }}
                          value={filters.gpFilterValue}
                          onChange={(v) => setFilters(prev => ({ ...prev, gpFilterValue: v == null ? null : Number(v) }))}
                          addonAfter="%"
                          autoFocus
                        />
                      </div>
                    )}
                  </>
                )}
              />
              <Select
                placeholder="STC status"
                popupMatchSelectWidth={false}
                allowClear
                style={{ width: 200 }}
                value={filters.stcStatus}
                onChange={(v) => handleFilter('stcStatus', v)}
                options={[
                  { label: 'STC collected', value: 'collected' },
                  { label: 'Awaiting STC', value: 'awaiting' },
                  { label: 'Has state subsidy', value: 'subsidy' },
                ]}
              />
            </Space>
            {chartFilter && (
              <Tag closable onClose={() => setChartFilter(null)} color="blue" style={{ fontSize: 13, margin: 0 }}>
                {chartFilter.label}
              </Tag>
            )}
          </div>
        </Card>

        {/* Single month: 3 pie charts (State, System Type, Payment Method) */}
        {isSingleMonth && projects.length > 0 && (
          <Row gutter={[16, 0]}>
            {renderPie(pieByState, 'Revenue by State', 'state')}
            {renderPie(pieBySystem, 'Revenue by System Type', 'systemType')}
            {renderPie(pieByPayment, 'Revenue by Payment Method', 'paymentMethod')}
          </Row>
        )}

        {/* Revenue over time, beside how long the welcome pack takes */}
        {((!isSingleMonth && lineChartData.length > 0) || histWelcomePack.rows.length > 0) && (
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            {!isSingleMonth && lineChartData.length > 0 && (
              <Col xs={24} md={12}>
                <Card
                  size="small"
                  title="Monthly Revenue"
                  /* Which date the months are counted on, because the same
                     projects fall in different months under each one. */
                  extra={(
                    <span style={{ fontSize: 11, color: '#999' }}>
                      by {dateField === 'welcomePackAt' ? 'welcome pack' : 'closing'} month
                    </span>
                  )}
                  style={{ height: '100%' }}
                  styles={{ body: { height: CHART_BOX, paddingTop: 8 } }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={lineChartData} margin={{ top: 5, right: 16, bottom: 28, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" fontSize={10} />
                      <YAxis tickFormatter={(v) => fmt(v)} fontSize={10} />
                      <Tooltip formatter={(v, name) => [fmt(v), name]} />
                      <Legend />
                      <Line type="monotone" dataKey="Revenue" stroke="#54A2CC" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            )}
            {histWelcomePack.rows.length > 0 && (
              <Col xs={24} md={(!isSingleMonth && lineChartData.length > 0) ? 12 : 24}>
                <Card
                  size="small"
                  title="Welcome pack — waiting vs taken (days)"
                  extra={(
                    <span style={{ fontSize: 11, color: '#999' }}>
                      {histWelcomePack.awaiting} awaiting · {histWelcomePack.completed} issued
                    </span>
                  )}
                  style={{ height: '100%' }}
                  styles={{ body: { height: CHART_BOX, paddingTop: 8 } }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={histWelcomePack.rows} margin={{ top: 5, right: 16, bottom: 28, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" fontSize={10} interval={0} />
                      <YAxis allowDecimals={false} fontSize={10} />
                      <Tooltip />
                      <Legend />
                      {/* Each series filters the page on its own terms: the
                          amber one on how long a project has been waiting, the
                          blue one on how long it took. */}
                      {/* Each Bar carries its own fill as well as its Cells:
                          the Cells colour the bars, but the legend swatch reads
                          the Bar's fill and renders black without one. */}
                      <Bar
                        dataKey="Awaiting"
                        name="Signed, no welcome pack yet (days so far)"
                        fill="#FAB005"
                        radius={[3, 3, 0, 0]}
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => handleChartClick('wpAwaiting', e.range, `Awaiting WP: ${e.range}d`)}
                      >
                        {histWelcomePack.rows.map((r) => (
                          <Cell
                            key={r.range}
                            fill="#FAB005"
                            opacity={chartFilter?.type === 'wpAwaiting' && chartFilter?.value !== r.range ? 0.3 : 1}
                          />
                        ))}
                      </Bar>
                      <Bar
                        dataKey="Completed"
                        name="Welcome pack issued (sale → WP)"
                        fill="#54A2CC"
                        radius={[3, 3, 0, 0]}
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => handleChartClick('wpTaken', e.range, `Sale→WP: ${e.range}d`)}
                      >
                        {histWelcomePack.rows.map((r) => (
                          <Cell
                            key={r.range}
                            fill="#54A2CC"
                            opacity={chartFilter?.type === 'wpTaken' && chartFilter?.value !== r.range ? 0.3 : 1}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            )}
          </Row>
        )}

        {/* Duration distribution charts */}
        {(histToJob.length > 0 || histJobToWP.length > 0) && (
          <Row gutter={[16, 16]}>
            {histToJob.length > 0 && (
              <Col xs={24} md={12}>
                <Card
                  size="small"
                  title="Sale → Job Distribution (days)"
                  style={{ marginTop: 16, height: 'calc(100% - 16px)' }}
                  styles={{ body: { height: CHART_BOX, paddingTop: 8 } }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={histToJob} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" fontSize={10} />
                      <YAxis allowDecimals={false} fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="count" radius={[3, 3, 0, 0]} name="Projects" onClick={(entry) => handleChartClick('saleToJob', entry.range, `Sale→Job: ${entry.range}d`)} style={{ cursor: 'pointer' }}>
                        {histToJob.map((entry, i) => (
                          <Cell key={i} fill="#54A2CC" opacity={chartFilter?.type === 'saleToJob' && chartFilter?.value !== entry.range ? 0.3 : 1} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            )}
            {histJobToWP.length > 0 && (
              <Col xs={24} md={12}>
                <Card
                  size="small"
                  title="Job → Welcome Pack Distribution (days)"
                  style={{ marginTop: 16, height: 'calc(100% - 16px)' }}
                  styles={{ body: { height: CHART_BOX, paddingTop: 8 } }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={histJobToWP} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" fontSize={10} />
                      <YAxis allowDecimals={false} fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="count" radius={[3, 3, 0, 0]} name="Projects" onClick={(entry) => handleChartClick('jobToWP', entry.range, `Job→WP: ${entry.range}d`)} style={{ cursor: 'pointer' }}>
                        {histJobToWP.map((entry, i) => (
                          <Cell key={i} fill="#f5b759" opacity={chartFilter?.type === 'jobToWP' && chartFilter?.value !== entry.range ? 0.3 : 1} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            )}
          </Row>
        )}

        {/* Projects Table */}
        <Card style={{ marginTop: 16 }} title={tableTitle}>
          <Table
            dataSource={filteredProjects}
            columns={columns}
            rowKey="projectId"
            expandable={{
              expandedRowRender,
              expandedRowKeys,
              // Several rows can now be open at once, so a row toggles itself
              // rather than replacing whatever was open
              onExpand: (expanded, record) => {
                setExpandedRowKeys((prev) => expanded
                  ? [...prev, record.projectId]
                  : prev.filter((k) => k !== record.projectId));
              },
              // Expand-all sets a key for every filtered project, not just the
              // visible page: antd only mounts the current page's panels, so the
              // rest cost nothing and stay open as you page through.
              // Reuses antd's own expand-icon markup so the header control is
              // pixel-identical to the per-row ones rather than merely centred.
              // There is no "-expanded" class: the base class draws a minus, and
              // "-collapsed" rotates the second bar upright to make a plus.
              // A native title, not a Tooltip - in this file Tooltip is recharts',
              // which renders nothing outside a chart.
              columnTitle: (
                <div style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    className={`ant-table-row-expand-icon${allExpanded ? '' : ' ant-table-row-expand-icon-collapsed'}`}
                    title={allExpanded ? 'Collapse all' : 'Expand all'}
                    aria-label={allExpanded ? 'Collapse all' : 'Expand all'}
                    aria-expanded={allExpanded}
                    onClick={() => {
                      if (allExpanded) { setExpandedRowKeys([]); setDetailCategory(null); }
                      else setExpandedRowKeys(filteredProjects.map((p) => p.projectId));
                    }}
                    style={{ display: 'inline-flex', float: 'none', verticalAlign: 'sub' }}
                  />
                </div>
              ),
            }}
            pagination={{
              pageSize,
              showSizeChanger: true,
              showTotal: (t) => `${t} projects`,
              onChange: (page, size) => setPageSize(size),
              onShowSizeChange: (current, size) => setPageSize(size),
            }}
            size="small"
            scroll={{ x: 1700 }}
          />
        </Card>
      </Spin>
            </>
          ),
        }, {
          key: 'pipeline',
          label: 'Current Pipeline',
          children: <CurrentPipeline />,
        }, {
          key: 'trending',
          label: 'Trending',
          children: <Trending />,
        }]}
      />
      <AIChatBox
        endpoint="/api/retail-finance/chat"
        contextBuilder={() => {
          const fp = filteredProjects;
          const stats = fs;
          let ctx = `SH Residential Summary: ${stats.projectCount || 0} projects, Total CRM Revenue: $${Math.round(stats.totalRevenue || 0).toLocaleString()}, Total Acct GP: $${Math.round(stats.totalGp || 0).toLocaleString()}, Acct GP%: ${stats.totalGpPct != null ? stats.totalGpPct.toFixed(1) : '-'}%\n`;
          ctx += `PV: ${stats.totalPvSize?.toFixed(1) || 0}kW, Battery: ${stats.totalBatterySize?.toFixed(1) || 0}kWh\n\n`;
          ctx += 'Project details (top 50):\nID | Customer | State | System | Payment | CRM Rev | Acct Rev | Acct GP | GP%\n';
          for (const p of fp.slice(0, 50)) {
            ctx += `${p.projectId} | ${p.customerName || ''} | ${p.state || ''} | ${p.systemType || ''} | ${p.paymentMethod || ''} | $${Math.round(p.revenue || 0)} | $${Math.round(p.acctRevenue || 0)} | $${Math.round(p.gp || 0)} | ${p.gpPct != null ? p.gpPct.toFixed(1) + '%' : '-'}\n`;
          }
          if (fp.length > 50) ctx += `... and ${fp.length - 50} more projects\n`;
          return ctx;
        }}
        title="SH Residential Analyst"
        placeholder="Ask about project data, e.g. 'Which projects have low GP%?'"
      />
    </PageLayout>
  );
}
